import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Sidebar from '../Sidebar';
import LogoutButton from '../Login/LogoutButton';
import FileUploadSection from './FileUploadSection';
import CriteriaForm from './CriteriaForm';
import JobDescriptionForm from './JobDescriptionForm';
import { useFormValidation, useAuthentication } from './customHooks';

const ResumeUploadForm = () => {
    const navigate = useNavigate();
    
    // Authentication
    const { authError, setAuthError, isCheckingAuth, setIsCheckingAuth, checkAuth } = useAuthentication(navigate);
    
    // Processing mode state
    const [processingMode, setProcessingMode] = useState('manual');
    
    // Form State
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [formData, setFormData] = useState({
        domain: '',
        experience: '',
        jobTitle: '',
        requiredSkills: '',
        exclusionCriteria: ''
    });
    
    // Job Description state
    const [jobDescriptionData, setJobDescriptionData] = useState({
        jobDescription: '',
        exclusionCriteria: ''
    });
    
    // Processing State - Simplified
    const [processingState, setProcessingState] = useState('idle'); // 'idle', 'uploading', 'processing', 'completed', 'error'
    const [processingError, setProcessingError] = useState(null);
    const [processedCandidates, setProcessedCandidates] = useState([]);
    
    // Authentication Check
    useEffect(() => {
        const isAuthenticated = checkAuth();
        setIsCheckingAuth(false);
        
        if (!isAuthenticated) {
            return;
        }
    }, [navigate, checkAuth, setIsCheckingAuth]);

    // Custom Hooks
    const { errors, validateForm, setErrors } = useFormValidation(
        formData, 
        selectedFiles, 
        processingMode, 
        jobDescriptionData
    );

    // Upload function with direct processing
    const uploadResumes = async (formData) => {
        // console.log('🚀 uploadResumes function called');
        // console.log('📤 FormData contents:');
        
        // Log FormData contents for debugging
        for (let [key, value] of formData.entries()) {
            if (value instanceof File) {
                console.log(`  ${key}: File(${value.name}, ${value.size} bytes)`);
            } else {
                console.log(`  ${key}: ${value}`);
            }
        }
        
        const token = localStorage.getItem('adminToken');
        if (!token) {
            throw new Error('Authentication token missing');
        }

        // console.log('🌐 Making upload request to backend...');
        
        const response = await fetch('https://n8n.acespireconsulting.com/webhook/upload-resumes', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        // console.log('📥 Upload response received:', {
        //     status: response.status,
        //     statusText: response.statusText,
        //     ok: response.ok
        // });

        if (response.status === 401 || response.status === 403) {
            throw new Error('Authentication failed');
        }

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Error response:', errorText);
            throw new Error(`Upload failed: ${response.status} ${errorText}`);
        }

        const responseText = await response.text();
        //console.log('📄 Raw success response:', responseText);
        
        let result;
        if (responseText.trim() === '') {
            result = { success: true, message: 'Upload completed' };
        } else {
            result = JSON.parse(responseText);
        }

        // console.log('✅ Upload completed successfully:', result);
        return result;
    };

    // Fetch candidates function
    const fetchCandidates = async () => {
        // console.log('📊 fetchCandidates function called');
        
        const token = localStorage.getItem('adminToken');
        if (!token) {
            throw new Error('Authentication token missing');
        }

        const response = await fetch('https://n8n.acespireconsulting.com/webhook/get-candidates', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });

        if (response.status === 401 || response.status === 403) {
            throw new Error('Authentication failed');
        }

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseText = await response.text();
        if (!responseText.trim()) {
            return [];
        }

        const data = JSON.parse(responseText);
        
        // Handle your API's array format
        let candidatesData = [];
        if (Array.isArray(data) && data.length > 0 && data[0]) {
            const responseObj = data[0];
            if (Array.isArray(responseObj.data)) {
                candidatesData = responseObj.data;
            } else if (Array.isArray(responseObj)) {
                candidatesData = responseObj;
            } else {
                candidatesData = data;
            }
        } else if (Array.isArray(data)) {
            candidatesData = data;
        }

        // console.log('📊 Candidates fetched:', candidatesData.length);
        return candidatesData;
    };

    // Infinite polling function - no timeouts
    const pollForCompletion = async () => {
        let pollAttempts = 0;
        let timeoutId = null;
        
        const poll = async () => {
            try {
                pollAttempts++;
                // console.log(`🔄 Polling attempt ${pollAttempts}`);
                
                const candidates = await fetchCandidates();
                // console.log(`📊 Found ${candidates.length}/${selectedFiles.length} candidates`);
                
                // Update progress in real-time
                setProcessedCandidates(candidates);
                
                // Check for completion
                if (candidates.length >= selectedFiles.length) {
                    // console.log('✅ All candidates processed!');
                    setProcessingState('completed');
                    
                    toast.success(`All ${selectedFiles.length} resumes processed successfully!`, {
                        position: "top-right",
                        autoClose: 3000,
                    });
                    
                    // Navigate to dashboard immediately
                    navigate('/dashboard');
                    return;
                }
                
                // Continue polling - schedule next poll in 5 seconds
                timeoutId = setTimeout(poll, 5000);
                
            } catch (pollError) {
                console.error('❌ Backend error during polling:', pollError);
                
                // Only stop polling on actual backend errors
                setProcessingState('error');
                setProcessingError(pollError.message);
                
                // Handle authentication errors
                if (pollError.message.includes('Authentication failed')) {
                    setAuthError('Authentication failed');
                    navigate('/login', { replace: true });
                    return;
                }
                
                // Show backend error to user
                toast.error(`Backend Error: ${pollError.message}`, {
                    position: "top-right",
                    autoClose: 10000,
                });
            }
        };
        
        // Cleanup function
        const cleanup = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };
        
        // Store cleanup function for component unmount
        window.currentPollingCleanup = cleanup;
        
        // Start first poll after 3 seconds
        timeoutId = setTimeout(poll, 3000);
    };

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            if (window.currentPollingCleanup) {
                window.currentPollingCleanup();
                window.currentPollingCleanup = null;
            }
        };
    }, []);

    // Form Handlers
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleJobDescriptionChange = (field, value) => {
        setJobDescriptionData(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleModeChange = (newMode) => {
        // console.log('🔄 Switching processing mode from', processingMode, 'to', newMode);
        setProcessingMode(newMode);
        setErrors({});
        setProcessingError(null);
        
        if (newMode === 'manual') {
            setJobDescriptionData({ 
                jobDescription: '',
                exclusionCriteria: ''
             });
        } else {
            setFormData({
                domain: '',
                experience: '',
                jobTitle: '',
                requiredSkills: '',
                exclusionCriteria: ''
            });
        }
    };

    // Main submit handler with infinite polling
    const handleSubmit = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
        setAuthError('Authentication expired');
        navigate('/login', { replace: true });
        return;
    }

    if (!validateForm()) {
        const errorMessage = processingMode === 'job_description' 
            ? 'Please provide a job description and upload at least one resume.'
            : 'Please fill in all required fields and upload at least one resume.';
        
        toast.error(errorMessage, { position: "top-right", autoClose: 3000 });
        return;
    }

    // Reset state
    setProcessingState('uploading');
    setProcessingError(null);

    try {
        // console.log('📤 Starting upload process...');
        
        // Create FormData
        const uploadData = new FormData();
        
        if (processingMode === 'job_description') {
            uploadData.append('use_job_description', 'true');
            uploadData.append('job_description', jobDescriptionData.jobDescription);
            uploadData.append('exclusion_criteria', jobDescriptionData.exclusionCriteria || ''); 
        } else {
            uploadData.append('use_job_description', 'false');
            uploadData.append('job_title', formData.jobTitle);
            uploadData.append('experience_required', formData.experience);
            uploadData.append('required_skills', formData.requiredSkills);
            uploadData.append('additional_requirements', formData.domain);
            uploadData.append('exclusion_criteria', formData.exclusionCriteria || ''); 
        }

        selectedFiles.forEach((file) => {
            uploadData.append('resume_files', file);
        });

        // console.log('🔄 Processing resumes...');
        setProcessingState('processing');

        // Upload and wait for complete processing
        const uploadResult = await uploadResumes(uploadData);
        // console.log('✅ Processing completed successfully:', uploadResult);

        // Check if the backend indicates success - handle nested response structure
        if (uploadResult) {
            // console.log('📋 Processing completed - received response:', uploadResult);
            
            // Check if there's a success indicator anywhere in the response
            let hasSuccess = false;
            
            // Method 1: Check if response has success property directly
            if (uploadResult.success) {
                hasSuccess = true;
                // console.log('✅ Found success property directly');
            }
            
            // Method 2: Check if response is an object with success in keys
            if (typeof uploadResult === 'object' && !hasSuccess) {
                const responseString = JSON.stringify(uploadResult);
                if (responseString.includes('"success": true') || responseString.includes('"success":true')) {
                    hasSuccess = true;
                    // console.log('✅ Found success in response string');
                }
            }
            
            // Method 3: If we got any response without error, consider it success
            if (!hasSuccess) {
                hasSuccess = true; // Any response means processing completed
                // console.log('✅ Treating any response as success');
            }
            
            if (hasSuccess) {
                setProcessingState('completed');
                
                toast.success(`All ${selectedFiles.length} resumes processed successfully!`, {
                    position: "top-right",
                    autoClose: 2000,
                });

                // Navigate to dashboard after short delay
                setTimeout(() => {
                    // console.log('🚀 Navigating to dashboard...');
                    navigate('/dashboard');
                }, 2000);
            } else {
                throw new Error('Processing completed but success status not found');
            }
        } else {
            // console.log('⚠️ No upload result received');
            throw new Error('No response received from server');
        }

    } catch (error) {
        console.error('❌ Processing error:', error);
        setProcessingState('error');
        setProcessingError(error.message);
        
        if (error.message.includes('Authentication failed')) {
            setAuthError('Authentication failed during upload');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            navigate('/login', { replace: true });
            return;
        }
        
        toast.error(`Processing failed: ${error.message}`, {
            position: "top-right",
            autoClose: 5000,
        });
    }
};

    const handleReset = () => {
        // console.log('🔄 Resetting form state...');
        setProcessingState('idle');
        setProcessingError(null);
        setProcessedCandidates([]);
        setErrors({});
        
        // Cleanup any ongoing polling
        if (window.currentPollingCleanup) {
            window.currentPollingCleanup();
            window.currentPollingCleanup = null;
        }
    };

    // Simple progress display
    const getProgressMessage = () => {
        const processed = processedCandidates.length;
        const total = selectedFiles.length;
        
        if (processingState === 'uploading') {
            return 'Uploading files to server...';
        }
        
        if (processingState === 'processing') {
            return `Processing resumes... ${processed}/${total} completed`;
        }
        
        return '';
    };

    // Simplified error display - only shows backend errors
    const renderErrorState = () => {
        if (processingState !== 'error') return null;
        
        return (
            <div className="mb-8 p-6 rounded-lg border-2 bg-red-50 border-red-200">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-6 h-6 text-red-500">❌</div>
                    <span className="text-lg font-medium">Backend Error</span>
                </div>
                
                <div className="text-sm text-red-600 bg-white p-3 rounded-md mb-4">
                    {processingError}
                </div>
                
                <div className="flex gap-3 justify-center">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Go to Dashboard
                    </button>
                    <button
                        onClick={handleReset}
                        className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    };

    // Show loading screen while checking authentication
    if (isCheckingAuth) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <Clock className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                    <div className="text-gray-600 text-xl mb-4">Checking authentication...</div>
                    {authError && (
                        <div className="text-red-600 text-sm">{authError}</div>
                    )}
                </div>
            </div>
        );
    }

    const isProcessing = processingState === 'uploading' || processingState === 'processing';

    return (
        <div className="flex min-h-screen" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <Sidebar />

            <div className="flex-1 bg-white overflow-hidden">
                {/* Header */}
                <div className="p-8 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h1 className="text-2xl font-semibold text-gray-800">Hireon AI</h1>
                        {authError && (
                            <div className="text-red-600 text-sm bg-red-50 px-3 py-1 rounded-md border border-red-200">
                                ⚠️ {authError}
                            </div>
                        )}
                    </div>
                    <LogoutButton />
                </div>

                {/* Content */}
                <div className="p-8 flex flex-col items-center justify-center min-h-[600px]">
                    <div className="w-full max-w-4xl text-center">
                        <h2 className="text-4xl font-bold text-gray-800 mb-12">Let's Create A Criteria Filter</h2>

                        {/* Error State */}
                        {renderErrorState()}

                        {/* Processing Status */}
                        {(processingState === 'uploading' || processingState === 'processing') && (
                            <div className="mb-8 p-6 rounded-lg border-2 bg-blue-50 border-blue-200">
                                <div className="flex items-center justify-center gap-3 mb-4">
                                    <Clock className="w-6 h-6 text-blue-500 animate-spin" />
                                    <span className="text-lg font-medium">{getProgressMessage()}</span>
                                </div>

                                <div className="mb-4">
                                    <div className="bg-white rounded-full h-2 overflow-hidden">
                                        <div
                                            className="bg-blue-500 h-full transition-all duration-500"
                                            style={{ 
                                                width: `${
                                                    processingState === 'uploading' 
                                                        ? 30 
                                                        : 30 + ((processedCandidates.length / selectedFiles.length) * 70)
                                                }%` 
                                            }}
                                        />
                                    </div>
                                    <div className="text-sm text-gray-600 mt-2">
                                        {processingState === 'uploading' 
                                            ? 'Files uploaded, starting processing...' 
                                            : `${processedCandidates.length} / ${selectedFiles.length} resumes processed`
                                        }
                                    </div>
                                </div>

                                {/* <div className="text-sm text-blue-600">
                                    This may take 20-25 minutes for large batches. Please keep this tab open.
                                </div> */}
                            </div>
                        )}

                        {/* Form */}
                        <div className={isProcessing ? 'opacity-50 pointer-events-none' : ''}>
                            {/* Mode Selection Toggle */}
                            <div className="mb-8">
                                <div className="flex justify-center items-center gap-4 mb-6">
                                    <button
                                        onClick={() => handleModeChange('manual')}
                                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                                            processingMode === 'manual'
                                                ? 'bg-[#275F97] text-white shadow-lg'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                        disabled={isProcessing}
                                    >
                                        Manual Criteria
                                    </button>
                                    <div className="text-gray-400 font-medium">OR</div>
                                    <button
                                        onClick={() => handleModeChange('job_description')}
                                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                                            processingMode === 'job_description'
                                                ? 'bg-[#275F97] text-white shadow-lg'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                        disabled={isProcessing}
                                    >
                                        Job Description
                                    </button>
                                </div>
                            </div>

                            {/* Conditional Form Rendering */}
                            {processingMode === 'manual' ? (
                                <>
                                    <CriteriaForm
                                        formData={formData}
                                        errors={errors}
                                        isProcessing={isProcessing}
                                        onInputChange={handleInputChange}
                                    />

                                    <div className="mb-8">
                                        <input
                                            type="text"
                                            placeholder="Enter Domain/Industry Keywords (Optional)"
                                            value={formData.domain}
                                            onChange={(e) => handleInputChange('domain', e.target.value)}
                                            className="w-full max-w-2xl p-4 border-2 border-gray-200 rounded-xl text-lg outline-none focus:border-gray-400 transition-all"
                                            disabled={isProcessing}
                                        />
                                    </div>
                                </>
                            ) : (
                                <JobDescriptionForm
                                    jobDescriptionData={jobDescriptionData}
                                    errors={errors}
                                    isProcessing={isProcessing}
                                    onInputChange={handleJobDescriptionChange}
                                />
                            )}

                            {/* File Upload Section */}
                            <FileUploadSection
                                selectedFiles={selectedFiles}
                                setSelectedFiles={setSelectedFiles}
                                errors={errors}
                                setErrors={setErrors}
                                isProcessing={isProcessing}
                            />

                            {/* Generate Button */}
                            <button
                                onClick={handleSubmit}
                                disabled={isProcessing || authError}
                                className={`flex m-auto items-center gap-2 px-12 py-4 rounded-[40px] font-semibold text-lg transition-all ${
                                    isProcessing || authError
                                        ? 'bg-gray-400 text-white cursor-not-allowed'
                                        : 'bg-[#275F97] text-white hover:shadow-lg hover:bg-[#1e4a78]'
                                }`}
                            >
                                <span>
                                    {isProcessing ? 'Processing...' : 'Generate Scores'}
                                </span>
                                {!isProcessing && !authError && (
                                    <img
                                        src="/assets/star-icon.png"
                                        alt="Score Icon"
                                        className="w-5 h-5"
                                    />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default ResumeUploadForm;